export type Difference = {
  path: string;
  value1: unknown;
  value2: unknown;
};

/**
 * Performs a deep comparison between two objects and returns a list of all detected differences.
 *
 * This function recursively traverses objects and arrays to identify mismatches in structure
 * or value. Differences include:
 * - Properties with different primitive values
 * - Properties missing in one of the compared objects
 * - Type mismatches
 *
 * Each difference is reported with a fully qualified path indicating where the mismatch
 * occurred within the object tree.
 *
 * @param obj1 - The first value or object to compare.
 * @param obj2 - The second value or object to compare.
 * @param path - Internal parameter used to track the property path during recursion.
 *
 * @returns An array of differences. If the array is empty, the two values are deeply equal.
 *
 * @remarks
 * - Strict equality (`===`) is used as a fast-path check.
 * - `null` values are treated as primitives.
 * - Arrays are compared by index.
 * - Object key order does not affect the comparison.
 *
 * @example
 * Used to detect configuration changes, form diffs, or state comparisons.
 */
export function deepDiff(
  obj1: unknown,
  obj2: unknown,
  path: string = ''
): Difference[] {
  const differences: Difference[] = [];
  if (obj1 === obj2) {
    return differences;
  }

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    differences.push({
      path,
      value1: obj1,
      value2: obj2,
    });
    return differences;
  }

  const keys = new Set([
    ...Object.keys(obj1 as object),
    ...Object.keys(obj2 as object),
  ]);

  for (const key of keys) {
    const val1 = (obj1 as any)[key];
    const val2 = (obj2 as any)[key];

    const currentPath = path ? `${path}.${key}` : key;

    if (!(key in (obj1 as object))) {
      differences.push({
        path: currentPath,
        value1: undefined,
        value2: val2,
      });
      continue;
    }

    if (!(key in (obj2 as object))) {
      differences.push({
        path: currentPath,
        value1: val1,
        value2: undefined,
      });
      continue;
    }

    differences.push(...deepDiff(val1, val2, currentPath));
  }

  return differences;
}
